import { Container, InputField, PasswordField, SubmitButton } from '@/components/Auth/Buttons';
import { PasswordRequirements } from '@/components/Auth/PasswordRequirements';
import { useAuth } from '@/providers/AuthProvider';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { StyledLink } from '@/components/Auth/Links';

interface ResetPasswordInput {
  username: string;
  password: string;
  confirmationCode: string;
}

/**
 * ResetPassword component for resetting user password
 */
export default function ResetPassword() {
  const [loading, setLoading] = useState(false);
  const [resetInput, setResetInput] = useState<ResetPasswordInput>({
    username: '',
    password: '',
    confirmationCode: ''
  });
  const [error, setError] = useState<string | undefined>();
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();

  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.has('username')) {
      setResetInput((prev) => ({
        ...prev,
        username: searchParams.get('username') ?? '',
        confirmationCode: searchParams.get('code') ?? ''
      }));
    }
  }, [searchParams]);

  /**
   * Handles the password reset process
   */
  const resetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const normalizedUsername = resetInput.username.trim().toLowerCase();
      const trimmedConfirmationCode = resetInput.confirmationCode.trim();
      const trimmedPassword = resetInput.password.trim();

      await auth.confirmResetPassword({
        username: normalizedUsername,
        confirmationCode: trimmedConfirmationCode,
        newPassword: trimmedPassword
      });

      await auth.signIn({
        username: normalizedUsername,
        password: trimmedPassword
      });
      navigate('/');
    } catch (err: any) {
      setError(err.message);
      console.error('Error during password reset:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggles password visibility
   */
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  /**
   * Updates the reset input state
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setLoading(false);
    const { name, value } = e.target;
    setResetInput((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Container
      title="Set Your New Password"
      subtitle={'Check your email for the confirmation code.'}
      banner={error}
      className="relative md:w-600 px-20 py-32 md:px-100 md:py-32 rounded-[12px] bg-white backdrop-blur-xs shadow-xs z-1"
    >
      <form className="w-full flex flex-col space-y-12" onSubmit={resetPassword}>
        <InputField
          id="username"
          name="username"
          label="Email Address"
          value={resetInput.username}
          onChange={handleInputChange}
        />
        <InputField
          id="confirmationCode"
          name="confirmationCode"
          label="Confirmation Code"
          value={resetInput.confirmationCode}
          onChange={handleInputChange}
        />
        <div className="group">
          <PasswordField
            label="New Password"
            value={resetInput.password}
            onChange={handleInputChange}
            showPassword={showPassword}
            togglePasswordVisibility={togglePasswordVisibility}
          />
          <div className="hidden group-focus-within:block">
            <PasswordRequirements password={resetInput.password} />
          </div>
        </div>
        <SubmitButton title="Reset Password" loading={loading} onSubmit={resetPassword} />
        <div className="mt-24">
          <StyledLink
            to={'mailto:info@scribl.co'}
            label="Need help? Reach out to"
            linkText="info@scribl.co"
          />
        </div>
      </form>
    </Container>
  );
}
